import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mobile navigation (flat structure as requested)
  const mobileNavigation = [
    { name: 'Home', href: '/' },
    { name: 'Calendar', href: '/calendar' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Safeguarding', href: '/safeguarding' },
    { name: 'Reflections', href: '/reflections' },
    { name: 'Newsletters', href: '/newsletters' },
    { name: 'Meeting Notes', href: '/meeting-notes' },
    { name: 'Lectio Divina', href: '/lectio-divina' },
    { name: 'RCIA', href: '/rcia' },
    { name: 'Our History', href: '/history' },
    { name: 'Parish Team', href: '/team' },
    { name: 'Ministries', href: '/ministries' },
  ];

  // Desktop navigation with dropdowns
  const desktopNavigation = [
    { name: 'Home', href: '/' },
    { name: 'Calendar', href: '/calendar' },
    { name: 'Gallery', href: '/gallery' },
    {
      name: 'About Us',
      dropdown: [
        { name: 'Our History', href: '/history' },
        { name: 'Parish Team', href: '/team' },
        { name: 'Ministries', href: '/ministries' },
        { name: 'Safeguarding', href: '/safeguarding' },
      ]
    },
    {
      name: 'Resources',
      dropdown: [
        { name: 'Reflections', href: '/reflections' },
        { name: 'Newsletters', href: '/newsletters' },
        { name: 'Meeting Notes', href: '/meeting-notes' },
        { name: 'Lectio Divina', href: '/lectio-divina' },
        { name: 'RCIA', href: '/rcia' },
      ]
    },
  ];

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleDropdownOpen = (name: string) => {
    clearCloseTimeout();
    setActiveDropdown(name);
  };

  const handleDropdownClose = (immediate = false) => {
    clearCloseTimeout();
    if (immediate) {
      setActiveDropdown(null);
      return;
    }
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  return (
    <header className="bg-church-blue shadow-lg" role="banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo and Title */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <div className="text-white">
                <h1 className="text-2xl font-serif font-bold">All Saints Catholic Church</h1>
                <p className="text-church-gold text-sm">A Community of Faith, Hope & Love</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4" role="navigation" aria-label="Main navigation">
            {desktopNavigation.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.dropdown && handleDropdownOpen(item.name)}
                onMouseLeave={() => item.dropdown && handleDropdownClose()}
              >
                {item.dropdown ? (
                  <div className="relative">
                    <button
                      className="flex items-center text-white hover:text-church-gold transition-colors duration-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-church-gold rounded px-1 py-1"
                      aria-expanded={activeDropdown === item.name}
                      aria-haspopup="true"
                      onClick={() => activeDropdown === item.name ? handleDropdownClose(true) : handleDropdownOpen(item.name)}
                      onFocus={() => handleDropdownOpen(item.name)}
                    >
                      {item.name}
                      <ChevronDownIcon className="ml-1 h-4 w-4" />
                    </button>
                    
                    {activeDropdown === item.name && (
                      <div
                        className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50"
                        onMouseEnter={() => handleDropdownOpen(item.name)}
                        onMouseLeave={() => handleDropdownClose()}
                      >
                        <div className="py-1">
                          {item.dropdown.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-church-blue transition-colors duration-200"
                              onClick={() => handleDropdownClose(true)}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className="text-white hover:text-church-gold transition-colors duration-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-church-gold rounded px-1 py-1"
                    onClick={() => handleDropdownClose(true)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              className="text-white hover:text-church-gold focus:outline-none focus:ring-2 focus:ring-church-gold rounded-md p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label="Toggle navigation menu"
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav
            id="mobile-menu"
            className="md:hidden pb-4"
            role="navigation"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col space-y-2">
              {mobileNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-white hover:text-church-gold transition-colors duration-200 font-medium px-2 py-2 rounded focus:outline-none focus:ring-2 focus:ring-church-gold"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
